import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { ROUTES } from '@/constants/routes'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import {
  Box,
  Heading,
  HStack,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { LeadCard } from '../components/LeadCard'
import { LeadsFilters } from '../components/LeadsFilters'
import { LeadsTable } from '../components/LeadsTable'
import { useDeleteLead, useLeads } from '../hooks/useLeads'

/**
 * Leads List Page
 * Main page for displaying and managing leads
 */
export function LeadsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [leadToDelete, setLeadToDelete] = useState(null)
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  // Use URL filters hook
  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    status: '',
    state: '',
    source: '',
    assigned_to: '',
    is_overdue: '',
    page: 1,
    page_size: 20,
    ordering: '-created_at',
  })

  // Fetch leads
  const { data, isLoading, error } = useLeads(filters)
  const leads = data?.data || []
  const pagination = data?.meta?.pagination || {}
  const totalCount = pagination.total_count || 0
  const pageCount = Math.ceil(totalCount / filters.page_size)

  // Delete lead mutation
  const deleteMutation = useDeleteLead()

  // Responsive view
  const isMobile = useBreakpointValue({ base: true, md: false })

  const handleFiltersChange = (newFilters) => {
    updateFilters(newFilters)
  }

  const handleResetFilters = () => {
    updateFilters({
      search: '',
      status: '',
      state: '',
      source: '',
      assigned_to: '',
      is_overdue: '',
      page: 1,
    })
  }

  const handleDeleteClick = (lead) => {
    setLeadToDelete(lead)
    onDeleteOpen()
  }

  const handleDeleteConfirm = async () => {
    if (leadToDelete) {
      await deleteMutation.mutateAsync(leadToDelete.uuid)
      setLeadToDelete(null)
      onDeleteClose()
    }
  }

  if (isLoading) {
    return <Spinner fullScreen />
  }

  if (error) {
    return (
      <EmptyState
        title="Error loading leads"
        description={ error.message || 'An error occurred while loading leads.' }
      />
    )
  }

  return (
    <Box>
      <VStack spacing={ 6 } align="stretch">
        {/* Header */}
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Heading size="lg" color="text.primary">Leads</Heading>
          <Button
            variant="solid"
            size="md"
            onClick={ () => navigate(ROUTES.LEADS_NEW) }
            bg="#0052CC"
            color="white"
            _hover={{ bg: '#0747A6' }}
            _active={{ bg: '#0747A6' }}
            px={ 4 }
            fontWeight="500"
          >
            <HStack spacing={ 2 }>
              <Box as={ FiPlus } boxSize="16px" />
              <span>{t('leads.newLead')}</span>
            </HStack>
          </Button>
        </HStack>

        {/* Filters */}
        <LeadsFilters
          filters={ filters }
          onFiltersChange={ handleFiltersChange }
          onReset={ handleResetFilters }
        />

        {/* Table or Cards */}
        {leads.length === 0 ? (
          <EmptyState
            title={t('leads.noLeads')}
            description={t('leads.noLeadsDesc')}
            actionLabel={t('leads.newLead')}
            onAction={ () => navigate(ROUTES.LEADS_NEW) }
          />
        ) : isMobile ? (
          <VStack spacing={ 4 } align="stretch">
            {leads.map((lead) => (
              <LeadCard key={ lead.uuid } lead={ lead } onDelete={ handleDeleteClick } />
            ))}
          </VStack>
        ) : (
          <LeadsTable
            data={ leads }
            isLoading={ isLoading }
            pageCount={ pageCount }
            totalCount={ totalCount }
            pagination={{
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            }}
            sorting={filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : []}
            onPaginationChange={ handlePaginationChange }
            onSortingChange={ handleSortingChange }
            onDelete={ handleDeleteClick }
          />
        )}
      </VStack>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={ isDeleteOpen }
        onClose={ onDeleteClose }
        onConfirm={ handleDeleteConfirm }
        title="Delete Lead"
        message={
          <>
            Are you sure you want to delete <strong>{ leadToDelete?.first_name } { leadToDelete?.last_name }</strong>?
            <br />
            <br />
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        isLoading={ deleteMutation.isLoading }
        colorScheme="red"
      />
    </Box>
  )
}

export default LeadsList
